import * as React from 'react'
import { FileText, Loader2 } from 'lucide-react'

import type { EmailTemplate } from '@/gen/cortado/v1/emails_pb'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Badge } from '@/components/ui/badge'

export type EmailTemplateCategory =
  | 'General'
  | 'Inquiry'
  | 'Booking'
  | 'Quote'
  | 'Invoice'

interface EmailTemplateInsertMenuProps {
  templates: EmailTemplate[]
  preferredCategory: EmailTemplateCategory
  isLoading?: boolean
  disabled?: boolean
  onSelectTemplate: (template: EmailTemplate) => void
}

function sameCategory(category: string, expected: EmailTemplateCategory) {
  return category.toLowerCase() === expected.toLowerCase()
}

function templateValue(template: EmailTemplate) {
  return [
    template.name,
    template.category,
    template.subject,
  ].join(' ')
}

export function EmailTemplateInsertMenu({
  templates,
  preferredCategory,
  isLoading = false,
  disabled = false,
  onSelectTemplate,
}: EmailTemplateInsertMenuProps) {
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const preferredTemplates = React.useMemo(
    () => templates.filter((template) => sameCategory(template.category, preferredCategory)),
    [preferredCategory, templates]
  )

  const generalTemplates = React.useMemo(
    () => {
      if (preferredCategory === 'General') return []
      return templates.filter((template) => sameCategory(template.category, 'General'))
    },
    [preferredCategory, templates]
  )

  const hasTemplates = preferredTemplates.length > 0 || generalTemplates.length > 0

  const handleSelect = (template: EmailTemplate) => {
    onSelectTemplate(template)
    setOpen(false)
  }

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || isLoading}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileText className="h-4 w-4" />
        )}
        Insert Template
      </Button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-[360px] rounded-md border bg-popover text-popover-foreground shadow-md outline-none"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              setOpen(false)
            }
          }}
        >
          <Command>
            <CommandInput
              autoFocus
              placeholder="Search templates..."
            />
            <CommandList className="max-h-80 overflow-y-auto">
              <CommandEmpty>
                {isLoading ? 'Loading templates...' : 'No matching templates'}
              </CommandEmpty>
              {hasTemplates && (
                <>
                  {preferredTemplates.length > 0 && (
                    <CommandGroup heading={`${preferredCategory} templates`}>
                      {preferredTemplates.map((template) => (
                        <TemplateItem
                          key={template.id}
                          template={template}
                          value={templateValue(template)}
                          onSelect={() => handleSelect(template)}
                        />
                      ))}
                    </CommandGroup>
                  )}
                  {preferredTemplates.length > 0 && generalTemplates.length > 0 && (
                    <CommandSeparator />
                  )}
                  {generalTemplates.length > 0 && (
                    <CommandGroup heading="General templates">
                      {generalTemplates.map((template) => (
                        <TemplateItem
                          key={template.id}
                          template={template}
                          value={templateValue(template)}
                          onSelect={() => handleSelect(template)}
                        />
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}

interface TemplateItemProps {
  template: EmailTemplate
  value: string
  onSelect: () => void
}

function TemplateItem({ template, value, onSelect }: TemplateItemProps) {
  return (
    <CommandItem value={value} onSelect={onSelect} className="items-start">
      <FileText className="mt-0.5 h-4 w-4" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{template.name}</span>
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {template.category}
          </Badge>
        </div>
        {template.subject && (
          <div className="truncate text-xs text-muted-foreground">
            {template.subject}
          </div>
        )}
      </div>
    </CommandItem>
  )
}
